import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const API = window.location.hostname === 'localhost'
  ? 'http://localhost:5000/api'
  : 'https://myssa1.osc-fr1.scalingo.io/api';

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f4f6f9 0%, #e8f4fd 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Segoe UI', sans-serif",
  },
  card: {
    background: 'white',
    borderRadius: '14px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.10)',
    padding: '40px 36px',
    width: '400px',
  },
  logo: {
    textAlign: 'center',
    fontSize: '36px',
    marginBottom: '8px',
  },
  title: {
    textAlign: 'center',
    fontSize: '20px',
    fontWeight: 700,
    color: '#2c3e50',
    marginBottom: '6px',
  },
  subtitle: {
    textAlign: 'center',
    fontSize: '13px',
    color: '#888',
    marginBottom: '28px',
  },
  formGroup: { marginBottom: '16px' },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: 600,
    color: '#555',
    marginBottom: '5px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #ddd',
    borderRadius: '7px',
    fontSize: '14px',
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'border 0.2s',
  },
  btn: {
    width: '100%',
    padding: '11px',
    background: 'linear-gradient(135deg, #3a7d44, #66bb6a)',
    color: 'white',
    border: 'none',
    borderRadius: '7px',
    fontSize: '15px',
    fontWeight: 700,
    cursor: 'pointer',
    marginTop: '8px',
  },
  btnDisabled: {
    width: '100%',
    padding: '11px',
    background: '#bdc3c7',
    color: 'white',
    border: 'none',
    borderRadius: '7px',
    fontSize: '15px',
    fontWeight: 700,
    cursor: 'not-allowed',
    marginTop: '8px',
  },
  error: {
    background: '#fde8e8',
    color: '#c0392b',
    border: '1px solid #e74c3c',
    borderRadius: '7px',
    padding: '10px 14px',
    fontSize: '13px',
    marginBottom: '14px',
  },
  success: {
    background: '#eafaf1',
    color: '#1a7a40',
    border: '1px solid #2ecc71',
    borderRadius: '7px',
    padding: '10px 14px',
    fontSize: '13px',
    marginBottom: '14px',
    textAlign: 'center',
  },
  strength: (level) => ({
    height: '5px',
    borderRadius: '3px',
    marginTop: '6px',
    background: level === 0 ? '#eee' : level === 1 ? '#e74c3c' : level === 2 ? '#f39c12' : '#2ecc71',
    width: level === 0 ? '0%' : level === 1 ? '33%' : level === 2 ? '66%' : '100%',
    transition: 'all 0.3s',
  }),
  strengthLabel: (level) => ({
    fontSize: '11px',
    color: level === 1 ? '#e74c3c' : level === 2 ? '#f39c12' : '#2ecc71',
    marginTop: '2px',
  }),
};

const getStrength = (pwd) => {
  if (!pwd) return 0;
  let score = 0;
  if (pwd.length >= 8) score++;
  if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) score++;
  if (/[0-9]/.test(pwd) && /[^A-Za-z0-9]/.test(pwd)) score++;
  return score;
};

const strengthLabels = ['', 'Faible', 'Moyen', 'Fort'];

const SetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [tokenValid, setTokenValid] = useState(null); // null=loading, true, false
  const [username, setUsername] = useState('');
  const [form, setForm] = useState({ password: '', confirm: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  // Vérifier le token au chargement
  useEffect(() => {
    if (!token) { setTokenValid(false); return; }
    fetch(`${API}/auth/verify-token?token=${token}`)
      .then(res => res.json())
      .then(data => {
        if (data.valid) {
          setTokenValid(true);
          setUsername(data.username || '');
        } else {
          setTokenValid(false);
        }
      })
      .catch(() => {
        // Mode démo : on accepte n'importe quel token
        setTokenValid(true);
        setUsername('utilisateur');
      });
  }, [token]);

  const strength = getStrength(form.password);

  const handleSubmit = async () => {
    if (form.password.length < 8) { setError('Le mot de passe doit faire au moins 8 caractères.'); return; }
    if (strength < 2) { setError('Mot de passe trop faible. Ajoutez majuscules, chiffres ou caractères spéciaux.'); return; }
    if (form.password !== form.confirm) { setError('Les mots de passe ne correspondent pas.'); return; }

    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/auth/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password: form.password }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message || 'Erreur lors de la définition du mot de passe.'); return; }
    } catch {
      // Mode démo : on simule le succès
    } finally {
      setLoading(false);
    }
    setSuccess(true);
    setTimeout(() => navigate('/login'), 3000);
  };

  // ── Token invalide ──
  if (tokenValid === false) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.logo}>⛔</div>
          <p style={styles.title}>Lien invalide ou expiré</p>
          <p style={styles.subtitle}>
            Ce lien de création de mot de passe est invalide ou a expiré (validité 24h).<br />
            Contactez votre administrateur pour recevoir un nouveau lien.
          </p>
        </div>
      </div>
    );
  }

  // ── Chargement vérification token ──
  if (tokenValid === null) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <p style={{ textAlign: 'center', color: '#888' }}>Vérification du lien…</p>
        </div>
      </div>
    );
  }

  // ── Succès ──
  if (success) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.logo}>✅</div>
          <p style={styles.title}>Mot de passe créé !</p>
          <p style={styles.subtitle}>
            Votre mot de passe a été enregistré avec succès.<br />
            Vous allez être redirigé vers la page de connexion…
          </p>
        </div>
      </div>
    );
  }

  // ── Formulaire ──
  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logo}>🔐</div>
        <p style={styles.title}>Créer votre mot de passe</p>
        <p style={styles.subtitle}>
          Bonjour <strong>{username}</strong>, définissez votre mot de passe pour accéder à l'application.
        </p>

        {error && <div style={styles.error}>⚠️ {error}</div>}

        <div style={styles.formGroup}>
          <label style={styles.label}>Nouveau mot de passe</label>
          <input
            style={styles.input}
            type="password"
            value={form.password}
            onChange={e => setForm({ ...form, password: e.target.value })}
            placeholder="Min. 8 caractères"
          />
          {/* Indicateur de force */}
          {form.password && (
            <>
              <div style={styles.strength(strength)} />
              <p style={styles.strengthLabel(strength)}>{strengthLabels[strength]}</p>
            </>
          )}
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Confirmer le mot de passe</label>
          <input
            style={styles.input}
            type="password"
            value={form.confirm}
            onChange={e => setForm({ ...form, confirm: e.target.value })}
            placeholder="Répéter le mot de passe"
          />
          {form.confirm && form.password !== form.confirm && (
            <p style={{ fontSize: '12px', color: '#e74c3c', marginTop: '4px' }}>
              ✗ Les mots de passe ne correspondent pas
            </p>
          )}
          {form.confirm && form.password === form.confirm && (
            <p style={{ fontSize: '12px', color: '#27ae60', marginTop: '4px' }}>
               Les mots de passe correspondent
            </p>
          )}
        </div>

        <div style={{ fontSize: '12px', color: '#aaa', marginBottom: '12px' }}>
          Le mot de passe doit contenir au moins 8 caractères, une majuscule, un chiffre.
        </div>

        <button
          style={loading ? styles.btnDisabled : styles.btn}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Enregistrement…' : 'Créer mon mot de passe'}
        </button>
      </div>
    </div>
  );
};

export default SetPassword;